import { apiClient } from '../utils/api-client';
import { throwForResponse } from './api-error';
import type {
  CategoriesListResponse,
  CategoryResponse,
  CategoryStrategyState,
  Stage,
  StageInput,
  StageResponse,
  StagesListResponse,
  TeamResponse,
  TeamsListResponse,
  Trophy,
  TrophyCategory,
  TrophyResponse,
  TrophyStatus,
  TrophyTeam,
  UpdateTrophyData,
} from '../types/trophy';

/** Who keeps categories and teams: a circuit, or an event of nobody's circuit. */
export type ClassOwner = 'trophy' | 'competition';

/** Where that owner's classes and squads live. */
function classesBase(owner: ClassOwner, slug: string): string {
  return owner === 'competition' ? `/api/competitions/${slug}` : `/api/trophies/${slug}`;
}

/**
 * The circuit and its stages. Reading is public — a trophy has a page like a
 * gym's; creating and moving stages is the trophy admins', enforced server-side.
 */
class TrophiesService {
  async getTrophyBySlug(slug: string): Promise<Trophy> {
    const response = await apiClient.get(`/api/trophies/${slug}`);

    if (!response.ok) {
      if (response.status === 404) {
        throw new Error('Trophy not found');
      }
      throw new Error('Failed to fetch trophy profile');
    }

    const data: TrophyResponse = await response.json();
    return data.data.attributes;
  }

  async updateTrophy(slug: string, attributes: UpdateTrophyData): Promise<Trophy> {
    const response = await apiClient.patch(`/api/trophies/${slug}`, {
      data: { attributes },
    });

    if (!response.ok) {
      await throwForResponse(response, 'Failed to update the trophy');
    }

    const data: TrophyResponse = await response.json();
    return data.data.attributes;
  }

  /**
   * Move the circuit through its life cycle. Its own endpoint because closing
   * one closes its whole season in the same breath — the panel asks before it
   * calls this. The server refuses a move the life cycle does not allow
   * (`invalid_transition`, `trophy_has_entries`).
   */
  async updateStatus(slug: string, status: TrophyStatus): Promise<Trophy> {
    const response = await apiClient.patch(`/api/trophies/${slug}/status`, {
      data: { attributes: { status } },
    });

    if (!response.ok) {
      await throwForResponse(response, 'Failed to change the status');
    }

    const data: TrophyResponse = await response.json();
    return data.data.attributes;
  }

  async followTrophy(slug: string): Promise<void> {
    const response = await apiClient.post(`/api/trophies/${slug}/follow`, {});

    if (!response.ok) {
      await throwForResponse(response, 'Failed to follow the trophy');
    }
  }

  async unfollowTrophy(slug: string): Promise<void> {
    const response = await apiClient.delete(`/api/trophies/${slug}/unfollow`);

    if (!response.ok) {
      await throwForResponse(response, 'Failed to unfollow the trophy');
    }
  }

  // The circuit's categories. Admin-only server-side: they are the shape of the
  // circuit, decided once, not the desk's day-to-day — which is why the roster
  // and the entry list are sent theirs rather than calling these.
  //
  // Every one of these takes the kind of place keeping them: a gym's own event
  // has no circuit to borrow from and keeps its own list, on the same
  // endpoints under `/competitions`.
  async listCategories(slug: string, owner: ClassOwner = 'trophy'): Promise<TrophyCategory[]> {
    const response = await apiClient.get(`${classesBase(owner, slug)}/categories`);

    if (!response.ok) {
      await throwForResponse(response, 'Failed to fetch the categories');
    }

    const data: CategoriesListResponse = await response.json();
    return data.data.map((item) => item.attributes);
  }

  async createCategory(slug: string, name: string, owner: ClassOwner = 'trophy'): Promise<TrophyCategory> {
    const response = await apiClient.post(`${classesBase(owner, slug)}/categories`, {
      data: { attributes: { name } },
    });

    if (!response.ok) {
      await throwForResponse(response, 'Failed to create the category');
    }

    const data: CategoryResponse = await response.json();
    return data.data.attributes;
  }

  async updateCategory(
    slug: string,
    categoryId: string,
    name: string,
    owner: ClassOwner = 'trophy'
  ): Promise<TrophyCategory> {
    const response = await apiClient.patch(
      `${classesBase(owner, slug)}/categories/${categoryId}`,
      { data: { attributes: { name } } }
    );

    if (!response.ok) {
      await throwForResponse(response, 'Failed to rename the category');
    }

    const data: CategoryResponse = await response.json();
    return data.data.attributes;
  }

  /** The athletes in it stay on the books; they simply lose their class. */
  async deleteCategory(slug: string, categoryId: string, owner: ClassOwner = 'trophy'): Promise<void> {
    const response = await apiClient.delete(
      `${classesBase(owner, slug)}/categories/${categoryId}`
    );

    if (!response.ok) {
      await throwForResponse(response, 'Failed to delete the category');
    }
  }

  /**
   * How entrants are put in the categories without the desk: the strategy
   * picked, every one on offer with its table drawn against these categories.
   * Admin-only, like the categories.
   */
  async getCategoryStrategy(slug: string, owner: ClassOwner = 'trophy'): Promise<CategoryStrategyState> {
    const response = await apiClient.get(`${classesBase(owner, slug)}/categories/strategy`);

    if (!response.ok) {
      await throwForResponse(response, 'Failed to fetch the category strategy');
    }

    const data: { data: CategoryStrategyState } = await response.json();
    return data.data;
  }

  /** Picks one, or none with null. Entries already made are left as they are. */
  async setCategoryStrategy(
    slug: string,
    strategy: string | null,
    owner: ClassOwner = 'trophy'
  ): Promise<CategoryStrategyState> {
    const response = await apiClient.put(`${classesBase(owner, slug)}/categories/strategy`, {
      data: { attributes: { strategy } },
    });

    if (!response.ok) {
      await throwForResponse(response, 'Failed to save the category strategy');
    }

    const data: { data: CategoryStrategyState } = await response.json();
    return data.data;
  }

  // The circuit's squads — what an athlete turns up with, and what the team
  // managers are named on. Same shape as its categories, but writing one is the
  // desk's too: the person typing the entries is who finds out that a club
  // nobody listed has sent three juniors.
  async listTeams(slug: string, owner: ClassOwner = 'trophy'): Promise<TrophyTeam[]> {
    const response = await apiClient.get(`${classesBase(owner, slug)}/teams`);

    if (!response.ok) {
      await throwForResponse(response, 'Failed to fetch the teams');
    }

    const data: TeamsListResponse = await response.json();
    return data.data.map((item) => item.attributes);
  }

  async createTeam(slug: string, name: string, owner: ClassOwner = 'trophy'): Promise<TrophyTeam> {
    const response = await apiClient.post(`${classesBase(owner, slug)}/teams`, {
      data: { attributes: { name } },
    });

    if (!response.ok) {
      await throwForResponse(response, 'Failed to create the team');
    }

    const data: TeamResponse = await response.json();
    return data.data.attributes;
  }

  async updateTeam(
    slug: string,
    teamId: string,
    name: string,
    owner: ClassOwner = 'trophy'
  ): Promise<TrophyTeam> {
    const response = await apiClient.patch(`${classesBase(owner, slug)}/teams/${teamId}`, {
      data: { attributes: { name } },
    });

    if (!response.ok) {
      await throwForResponse(response, 'Failed to rename the team');
    }

    const data: TeamResponse = await response.json();
    return data.data.attributes;
  }

  /**
   * Disbands a squad. With `moveTo` it is **folded into another** — its athletes
   * and the people named on it become that one's, which is the way out of the
   * same squad being written down twice. Without it the squad simply goes: the
   * athletes in it stay entered and climb for nobody.
   */
  async deleteTeam(
    slug: string,
    teamId: string,
    owner: ClassOwner = 'trophy',
    moveTo?: string | null
  ): Promise<void> {
    const query = moveTo ? `?move_to=${encodeURIComponent(moveTo)}` : '';
    const response = await apiClient.delete(`${classesBase(owner, slug)}/teams/${teamId}${query}`);

    if (!response.ok) {
      await throwForResponse(response, 'Failed to disband the team');
    }
  }

  async listStages(trophySlug: string): Promise<Stage[]> {
    const response = await apiClient.get(`/api/trophies/${trophySlug}/stages`);

    if (!response.ok) {
      await throwForResponse(response, 'Failed to fetch the stages');
    }

    const data: StagesListResponse = await response.json();
    return data.data.map((item) => item.attributes);
  }

  async createStage(trophySlug: string, attributes: StageInput): Promise<Stage> {
    const response = await apiClient.post(`/api/trophies/${trophySlug}/stages`, {
      data: { attributes },
    });

    if (!response.ok) {
      await throwForResponse(response, 'Failed to create the stage');
    }

    const data: StageResponse = await response.json();
    return data.data.attributes;
  }

  async updateStage(
    trophySlug: string,
    stageId: string,
    attributes: Partial<StageInput>
  ): Promise<Stage> {
    const response = await apiClient.patch(`/api/trophies/${trophySlug}/stages/${stageId}`, {
      data: { attributes },
    });

    if (!response.ok) {
      await throwForResponse(response, 'Failed to update the stage');
    }

    const data: StageResponse = await response.json();
    return data.data.attributes;
  }

  /**
   * Refused once anyone has entered the stage (`stage_has_participants`) — the
   * caller shows that message rather than a generic failure.
   */
  async deleteStage(trophySlug: string, stageId: string): Promise<void> {
    const response = await apiClient.delete(`/api/trophies/${trophySlug}/stages/${stageId}`);

    if (!response.ok) {
      await throwForResponse(response, 'Failed to delete the stage');
    }
  }
}

export const trophiesService = new TrophiesService();
